// AthleteDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as PmcChartReact } from 'react-chartjs-2';
import {
  Chart as ChartJSCore,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';

import ActivityDetailsModal from '../ActivityDetails/ActivityDetailsModal';
import AthleteCalendar from '../AthleteCalendar/AthleteCalendar';
import WeeklySummary from '../WeeklySummary/WeeklySummary';
import WeeklySummaryChart from '../WeeklySummary/WeeklySummaryChart';
import ActivityModal from '../ActivityModal/ActivityModal';
import CompetitionModal from '../CompetitionModal/CompetitionModal';
import useFetchEvents from '../hooks/useFetchEvents';
import useWeeklySummary from '../hooks/useWeeklySummary';
import { activityOptions, getActivityLabel as getActivityLabelUtil } from '../ActivityUtils/activityOptions';
import Swal from 'sweetalert2';
import './AthleteDashboard.css';
import WeekSelector from '../WeekSelector/WeekSelector';
import AthleteCoachBot from './AthleteCoachBot';
import { useI18n, useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';
import PMCPane from '../TrainerDashboard/PMCPane';
import WellnessVsCargaCard from '../TrainerDashboard/WellnessVsCarga/WellnessVsCargaCard';
import TrainingSummaryCard from './TrainingSummaryCard';
ChartJSCore.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

moment.locale('es');

const API_BASE = `${(process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '')}/api`;

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/** =========================
 * Helpers de seguridad UI
 * ========================= */
const isReactElement = (v) => !!(v && typeof v === 'object' && v.$$typeof && v.props);

const asSafeText = (v, fallback = '') => {
  if (v == null) return fallback;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return fallback;
};

const stripReactish = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const k of Object.keys(obj)) {
    const val = obj[k];
    if (k === 'planned' || k === 'completed' || k === 'metrics') {
      out[k] = val && typeof val === 'object' ? { ...val } : {};
      continue;
    }
    if (typeof val === 'function' || isReactElement(val)) continue;
    out[k] = val;
  }
  return out;
};

// Sanea TODO el array de eventos antes de pasarlos al calendario
const sanitizeEvents = (events = [], t) => {
  return (Array.isArray(events) ? events : []).map((ev) => {
    const copy = { ...ev };

    ['eventContent', 'renderEventContent', 'children'].forEach((key) => {
      if (key in copy) delete copy[key];
    });

    copy.title = asSafeText(copy.title, t('athleteDashboard.training'));

    const ext = stripReactish(copy.extendedProps || {});
    if ('icon' in ext) {
      const iconVal = ext.icon;
      if (typeof iconVal !== 'string') delete ext.icon;
    }
    if ('type' in ext) ext.type = asSafeText(ext.type, copy.title);
    if ('description' in ext) ext.description = asSafeText(ext.description, '');

    copy.extendedProps = ext;
    return copy;
  });
};

/** Render seguro de íconos (URL / JSX element / componente) */
function renderIcon(iconLike, { alt = 'icon', size = 16 } = {}) {
  const Placeholder = (
    <span
      aria-label={alt}
      className="inline-block rounded-sm bg-blue-200"
      style={{ width: size, height: size }}
    />
  );

  if (!iconLike) return Placeholder;

  if (typeof iconLike === 'string') {
    return (
      <img
        src={iconLike}
        alt={alt}
        width={size}
        height={size}
        style={{ display: 'inline-block' }}
        onError={(e) => {
          e.currentTarget.style.opacity = '0.2';
        }}
      />
    );
  }

  if (React.isValidElement(iconLike)) {
    return (
      <span aria-label={alt} style={{ display: 'inline-flex', width: size, height: size }}>
        {iconLike}
      </span>
    );
  }

  if (typeof iconLike === 'function') {
    try {
      const IconComp = iconLike;
      return (
        <span aria-label={alt} style={{ display: 'inline-flex', width: size, height: size }}>
          <IconComp size={size} />
        </span>
      );
    } catch {
      return Placeholder;
    }
  }

  return Placeholder;
}

/* ============================================================
   helper para normalizar siempre el objeto del modal
============================================================ */
const buildActivityForModal = (raw, getActivityLabel, sessionLabel) => {
  if (!raw) return null;

  if (typeof raw === 'string') {
    const type = raw;
    return {
      type,
      title: getActivityLabel(type),
      planned: {},
      completed: {},
      metrics: {},
    };
  }

  const type = raw.type || 'otro';
  const titleStable = (raw.title && String(raw.title).trim()) || getActivityLabel(type) || sessionLabel;

  return {
    ...raw,
    type,
    title: titleStable,
    planned: raw.planned || {},
    completed: raw.completed || {},
    metrics: raw.metrics || {},
  };
};

const toFiniteNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));


const getPlannedTss = (event) => {
  const planned = event?.extendedProps?.planned || {};
  return toFiniteNumber(planned.tss ?? planned.TSS ?? planned.trainingStressScore, 0);
};

const getNextSevenDayTss = (events = []) => {
  const start = moment().startOf('day');
  const end = start.clone().add(6, 'day').endOf('day');
  const byDay = new Map();

  events.forEach((event) => {
    const eventDate = moment(event?.start);
    if (!eventDate.isValid() || !eventDate.isBetween(start, end, null, '[]')) return;
    const key = eventDate.format('YYYY-MM-DD');
    byDay.set(key, (byDay.get(key) || 0) + getPlannedTss(event));
  });

  return Array.from({ length: 7 }, (_, index) => {
    const day = start.clone().add(index, 'day').format('YYYY-MM-DD');
    return byDay.get(day) || 0;
  });
};

const projectPmc = (latest, tssSeries, atlConstant = 7, ctlConstant = 42) => {
  if (!latest) return [];
  let ctl = toFiniteNumber(latest.ctl, 0);
  let atl = toFiniteNumber(latest.atl, 0);
  return tssSeries.map((tss, index) => {
    atl = atl + (toFiniteNumber(tss, 0) - atl) / atlConstant;
    ctl = ctl + (toFiniteNumber(tss, 0) - ctl) / ctlConstant;
    return {
      day: index + 1,
      ctl,
      atl,
      tsb: ctl - atl,
    };
  });
};

const AthleteDashboard = ({ isTrainerView = false, athleteId = null, onNavigateTo = null }) => {
  const { t } = useTranslation();
  const { language } = useI18n();
  moment.locale(language === 'en' ? 'en' : 'es');
  const navigate = useNavigate();

  const [userId, setUserId] = useState(() => (isTrainerView ? athleteId : localStorage.getItem('userId')));
  const [selectedWeek, setSelectedWeek] = useState(moment());

  useEffect(() => {
    const localId = localStorage.getItem('userId');
    if (!isTrainerView && !athleteId && localId && !userId) setUserId(localId);

    if (!isTrainerView && !athleteId && !localId) {
      console.warn('⚠️ No hay userId ni athleteId. Redirigiendo al login.');
      navigate('/login');
    }
  }, [athleteId, isTrainerView, userId, navigate]);

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    profilePicture: '',
  });

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showCompetitionModal, setShowCompetitionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleHideDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
  }, []);

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pmcRangeDays, setPmcRangeDays] = useState(90);
  const [pmcRows, setPmcRows] = useState([]);
  const [pmcLoading, setPmcLoading] = useState(false);
  const [pmcError, setPmcError] = useState('');

  // ✅ weekly summary desde DB/cache (modo estricto)
  const [weeklySummaryDB, setWeeklySummaryDB] = useState(null);
  const [weeklySummaryDBLoading, setWeeklySummaryDBLoading] = useState(false);

  // ✅ useFetchEvents SOLO CALENDARIO (sin summary local)
  const { events, setEvents, fetchEvents } = useFetchEvents(userId, selectedWeek);
  const weeklySummaryFromEvents = useWeeklySummary(events, selectedWeek);

  const activityTypeMapping = {
    Correr: 'correr',
    Ciclismo: 'ciclismo',
    Nadar: 'nadar',
    Descanso: 'día de descanso',
    Fuerza: 'fuerza',
    Caminar: 'caminar',
    Otro: 'otro',
    Competencia: 'competencia',
  };

  // ✅ fetch resumen backend
  const fetchWeeklySummaryDB = useCallback(async () => {
    if (!userId) return;

    setWeeklySummaryDBLoading(true);
    try {
      const week = moment(selectedWeek || moment()).startOf('isoWeek').format('YYYY-MM-DD');
      const url = `${API_BASE}/athletes/weekly-summary/${userId}?week=${encodeURIComponent(week)}`;
      const res = await axios.get(url, { withCredentials: true });
      setWeeklySummaryDB(res.data || null);
    } catch (err) {
      console.error('❌ Error weekly summary DB:', err?.response?.data || err?.message || err);
      setWeeklySummaryDB(null);
    } finally {
      setWeeklySummaryDBLoading(false);
    }
  }, [userId, selectedWeek]);

  useEffect(() => {
    if (!userId) return;
    fetchWeeklySummaryDB();
  }, [userId, selectedWeek, fetchWeeklySummaryDB]);

  useEffect(() => {
    if (!userId) return;
    let alive = true;

    const fetchPmc = async () => {
      setPmcLoading(true);
      setPmcError('');
      try {
        const end = moment().endOf('day').toISOString();
        const start = moment().subtract(pmcRangeDays, 'days').startOf('day').toISOString();
        await axios.post(`${API_BASE}/performance/recalculate/${userId}`, { start, end });
        const res = await axios.get(`${API_BASE}/performance/pmc/${userId}`, { params: { start, end } });
        const rows = (Array.isArray(res.data) ? res.data : [])
          .map((row) => ({
            date: row.date,
            ctl: toFiniteNumber(row.ctl, 0),
            atl: toFiniteNumber(row.atl, 0),
            tsb: toFiniteNumber(row.tsb, 0),
          }))
          .filter((row) => row.date);
        if (alive) setPmcRows(rows);
      } catch (err) {
        console.error('❌ Error PMC dashboard atleta:', err?.response?.data || err?.message || err);
        if (alive) {
          setPmcRows([]);
          setPmcError(t('common.genericError'));
        }
      } finally {
        if (alive) setPmcLoading(false);
      }
    };

    fetchPmc();
    return () => { alive = false; };
  }, [userId, pmcRangeDays, t]);

  const getActivityIcon = (type) => {
    const typeText = String(type || '');
    const activity = activityOptions.find((a) => a.type.toLowerCase() === typeText.toLowerCase());
    return activity ? activity.icon : activityOptions.find((a) => a.type === 'Otro').icon;
  };

  const getActivityLabel = (type) => getActivityLabelUtil(type, t);

  // Selección de fecha (nueva actividad)
  const handleDateSelect = (selectInfo) => {
    const m = moment(selectInfo.startStr);
    setSelectedWeek(m);

    const newDate = m.toISOString();
    setSelectedDate(newDate);
    setSelectedActivity(null);
    setShowActivityModal(true);
  };

  // Click en evento existente
  const handleEventClick = async (clickInfo) => {
    const activityId = clickInfo?.event?.id;
    if (!activityId) return;

    if (clickInfo?.event?.start) {
      setSelectedWeek(moment(clickInfo.event.start));
    }

    const eventDate = clickInfo.event.start ? clickInfo.event.start.toISOString() : null;

    if (!handleEventClick.__lastClick) handleEventClick.__lastClick = { id: null, t: 0 };
    const now = Date.now();
    if (handleEventClick.__lastClick.id === activityId && now - handleEventClick.__lastClick.t < 700) return;
    handleEventClick.__lastClick = { id: activityId, t: now };

    try {
      const res = await axios.get(`${API_BASE}/activities/${activityId}`);
      const activityData = res.data;

      const normalized = buildActivityForModal(
        {
          id: activityData._id,
          title: activityData.title,
          description: activityData.description,
          planned: activityData.planned,
          completed: activityData.completed,
          metrics: activityData.metrics,
          type: activityData.type || 'Otro',
          date: activityData.date || eventDate,
          bestEfforts:      activityData.bestEfforts      || [],
          hasHeartrate:     activityData.hasHeartrate     ?? false,
          isIndoor:         activityData.isIndoor         ?? false,
          stravaActivityId: activityData.stravaActivityId || null,
          gearName:         activityData.gearName         || null,
          normalizedPower:  activityData.normalizedPower  || null,
          calories:         activityData.calories         || 0,
        },
        getActivityLabel,
        t('athleteDashboard.session')
      );

      setSelectedActivity(normalized);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('❌ Error al cargar actividad desde el backend:', error);
    }
  };

  /* ============================================================
     Tarjeta compacta estilo TP (SOLO planificado)
  ============================================================ */
  const renderEventContent = (eventInfo) => {
    const ext = eventInfo.event.extendedProps || {};
    const rawType = ext.type || eventInfo.event.title || t('athleteDashboard.training');
    const typeLabel = getActivityLabel(rawType);
    const icon = getActivityIcon(rawType);

    const planned = ext.planned || {};
    const completed = ext.completed || {};

    const hasVal = (v) => !(v == null || v === '' || v === '0' || v === 0 || v === '00:00' || v === '00:00:00');

    const fmtDur = (v) => {
      if (!hasVal(v)) return '';
      if (typeof v === 'string') return v.length === 5 ? `${v}:00` : v;
      if (typeof v === 'number') return formatTime(v * 60);
      return '';
    };

    const addUnitsToDistance = (raw, sportType) => {
      if (!hasVal(raw)) return '';
      const str = String(raw).trim();
      if (/[a-zA-Z]/.test(str)) return str;

      const k = String(sportType || '').toLowerCase();
      if (k.includes('nad') || k.includes('swim')) return `${str} m`;
      if (k.includes('cor') || k.includes('run')) return `${str} km`;
      if (k.includes('cicl') || k.includes('bike')) return `${str} km`;
      if (k.includes('cam') || k.includes('walk')) return `${str} km`;
      return str;
    };

    const pDur = planned.duracion ?? planned.duration ?? null;
    const pDis = planned.distancia ?? planned.distance ?? null;

    let main = '';
    if (hasVal(pDur)) main = fmtDur(pDur);
    else if (hasVal(pDis)) main = addUnitsToDistance(pDis, rawType);
    else main = '-';

    const cDur = completed.duracion ?? completed.duration ?? null;
    const cDis = completed.distancia ?? completed.distance ?? null;
    const cTss = completed.tss ?? null;
    const isDone = hasVal(cDur) || hasVal(cDis) || hasVal(cTss);

    return (
      <div className={`tpwo ${isDone ? 'done' : 'plan'}`}>
        <div className="tpwo-head" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {renderIcon(icon, { alt: typeLabel, size: 16 })}
          <span>{typeLabel}</span>
          {isDone && (
            <span title={t('common.completed')} style={{ marginLeft: 'auto', fontWeight: 900, color: '#10b981' }}>
              ✓
            </span>
          )}
        </div>

        <div className="tpwo-main">{main}</div>
        <div className="tpwo-footer">
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
    );
  };

  const handleActivitySelection = (selectedType) => {
    const selectedAct = activityOptions.find((a) => a.type === selectedType);
    if (!selectedAct) return;

    const draft = buildActivityForModal(
      {
        type: selectedAct.type,
        title: getActivityLabel(selectedType, t),
        date: selectedDate ? new Date(selectedDate).toISOString() : new Date().toISOString(),
        planned: {},
        completed: {},
        metrics: {},
      },
      getActivityLabel,
      t('athleteDashboard.session')
    );

    if (selectedType === 'Competencia') {
      setShowActivityModal(false);
      setShowCompetitionModal(true);
      return;
    }
    setSelectedActivity(draft);
    setShowActivityModal(false);
    setTimeout(() => setShowDetailsModal(true), 300);
  };

  const handleSaveDetails = async (details) => {
    if (!selectedActivity?.type && !details?.type) return;

    const formattedDate =
      selectedActivity?.date || (selectedDate ? new Date(selectedDate).toISOString() : new Date().toISOString());
    const normalizedType = (details?.type || selectedActivity.type || '').toLowerCase().trim();

    const mappedType = Object.values(activityTypeMapping).includes(normalizedType)
      ? normalizedType
      : activityTypeMapping[selectedActivity.type] || 'otro';

    const userIdFinal = isTrainerView ? athleteId : localStorage.getItem('userId');
    if (!userIdFinal) {
      Swal.fire({
        icon: 'error',
        title: t('calendar.errorTitle'),
        text: t('athleteDashboard.errors.missingAthleteId'),
      });
      return;
    }

    const dataToSend = {
      assignedTo: userIdFinal,
      trainer: isTrainerView ? localStorage.getItem('userId') : null,
      title: details.title || selectedActivity.type || t('athleteDashboard.session'),
      description: details.description || '',
      date: formattedDate,
      planned: details.planned || {},
      completed: details.completed || {},
      metrics: details.metrics || {},
      type: mappedType,
    };

    const applyMetricAliases = (obj = {}) => {
      if (!obj || typeof obj !== 'object') return obj;

      // calories
      if (obj.calorias == null && obj.calories != null) obj.calorias = obj.calories;
      if (obj.calorias == null && obj.kcal != null) obj.calorias = obj.kcal;
      if (obj.calorias == null && obj.energyKcal != null) obj.calorias = obj.energyKcal;
      if (obj.calorias == null && obj.energy_kcal != null) obj.calorias = obj.energy_kcal;

      // distance
      if (obj.distance == null && obj.distancia != null) obj.distance = obj.distancia;
      if (obj.distance == null && obj.distanceKm != null) obj.distance = obj.distanceKm;
      if (obj.distance == null && obj.distance_km != null) obj.distance = obj.distance_km;

      // elevation gain
      if (obj.desnivel_positivo == null && obj.elevGain != null) obj.desnivel_positivo = obj.elevGain;
      if (obj.desnivel_positivo == null && obj.elevationGain != null) obj.desnivel_positivo = obj.elevationGain;
      if (obj.desnivel_positivo == null && obj.elevation_gain != null) obj.desnivel_positivo = obj.elevation_gain;
      if (obj.desnivel_positivo == null && obj.ascent != null) obj.desnivel_positivo = obj.ascent;
      if (obj.desnivel_positivo == null && obj.totalAscent != null) obj.desnivel_positivo = obj.totalAscent;

      // duration
      if (obj.duracion == null && obj.duration != null) obj.duracion = obj.duration;
      if (obj.duracion == null && obj.durationSec != null) obj.duracion = obj.durationSec;
      if (obj.duracion == null && obj.duration_sec != null) obj.duracion = obj.duration_sec;
      if (obj.duracion == null && obj.movingTime != null) obj.duracion = obj.movingTime;
      if (obj.duracion == null && obj.elapsedTime != null) obj.duracion = obj.elapsedTime;

      return obj;
    };

    dataToSend.planned = applyMetricAliases(dataToSend.planned);
    dataToSend.completed = applyMetricAliases(dataToSend.completed);

    try {
      if (selectedActivity?.id) {
        await axios.put(`${API_BASE}/activities/update/${selectedActivity.id}`, dataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        await axios.post(`${API_BASE}/activities/add`, dataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      Swal.fire({
        icon: 'success',
        title: t('athleteDashboard.success.activitySaved'),
        timer: 2000,
        showConfirmButton: false,
      });

      setSelectedActivity(null);

      if (typeof fetchEvents === 'function') {
        const m = moment(formattedDate);
        const start = m.clone().startOf('month').toDate();
        const end = m.clone().endOf('month').toDate();
        await fetchEvents(start, end);
      }

      // ✅ refresca resumen DB
      await fetchWeeklySummaryDB();
    } catch (error) {
      console.error('❌ Error al enviar la actividad al backend:', error?.response?.data || error.message);
      Swal.fire({
        icon: 'error',
        title: t('athleteDashboard.errors.saveErrorTitle'),
        text: error?.response?.data?.message || t('athleteDashboard.errors.saveError'),
      });
    }

    setShowDetailsModal(false);
  };

  const handleDelete = async (activityId) => {
    try {
      await axios.delete(`${API_BASE}/activities/delete/${activityId}`, { withCredentials: true });
      if (typeof setEvents === 'function') setEvents((prev) => prev.filter((e) => e.id !== activityId));
      if (typeof fetchEvents === 'function') await fetchEvents();

      // ✅ refresca resumen DB
      await fetchWeeklySummaryDB();
    } catch (error) {
      console.error('❌ Error al eliminar la actividad:', error?.response?.data || error.message);
    }
  };

  const handleEventDrop = async (eventDropInfo) => {
    const { event } = eventDropInfo;
    const activityId = event?.id;
    console.log("[drag&drop] activityId:", activityId, "newDate:", event?.startStr, "allDay:", event?.allDay);
    if (!activityId) return;

    const isAllDay = !!event.allDay;
    const newDate = isAllDay ? event.startStr : event.start?.toISOString() || event.startStr;

    const prevEvents = Array.isArray(events) ? [...events] : [];
    const nextEvents = prevEvents.map((e) =>
      String(e.id) === String(activityId) ? { ...e, start: newDate, end: newDate, date: newDate, allDay: isAllDay } : e
    );
    if (typeof setEvents === 'function') setEvents(nextEvents);

    try {
      await axios.put(
        `${API_BASE}/activities/update/${activityId}`,
        { date: newDate, allDay: isAllDay },
        { withCredentials: true }
      );

      // ✅ refresca resumen DB
      await fetchWeeklySummaryDB();
    } catch (error) {
      console.error('❌ Error al mover la actividad:', error?.response?.data || error.message);
      if (eventDropInfo?.revert) eventDropInfo.revert();
      Swal.fire(t('calendar.errorTitle'), t('athleteDashboard.errors.moveError'), 'error');
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/athletes/profile/${userId}`);
        const athlete = response.data.athlete;
        const profilePicture = athlete?.profilePhoto
          ? (athlete.profilePhoto.startsWith('data:') || athlete.profilePhoto.startsWith('http')
              ? athlete.profilePhoto
              : `${(process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '')}${athlete.profilePhoto}?t=${Date.now()}`)
          : `${(process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '')}/uploads/athletes/default.jpg`;

        setProfileData({
          firstName: athlete?.firstName || '',
          lastName: athlete?.lastName || '',
          profilePicture,
        });
      } catch (error) {
        console.error('❌ Error al cargar el perfil en el frontend:', error);
        setProfileData({
          profilePicture: `${(process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '')}/uploads/athletes/default.jpg`,
          firstName: t('athleteAdmin.name'),
          lastName: t('athleteDashboard.notAvailable'),
        });
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchProfileData();
  }, [userId, athleteId, isTrainerView, t]);

  const hasWeeklySummaryData = useCallback((summary) => {
    if (!summary) return false;
    const p = summary?.planned?.totalActivities || 0;
    const c = summary?.completed?.totalActivities || 0;
    return p > 0 || c > 0;
  }, []);

  // Prefer backend summary when populated; otherwise keep the previous real event-based behavior.
  const weeklySummaryToShow = hasWeeklySummaryData(weeklySummaryDB)
    ? weeklySummaryDB
    : weeklySummaryFromEvents;

  const showSummary = useMemo(() => {
    return hasWeeklySummaryData(weeklySummaryToShow);
  }, [weeklySummaryToShow, hasWeeklySummaryData]);

  const pmcLatest = pmcRows.length ? pmcRows[pmcRows.length - 1] : null;
  const tsbBarColor = (v) => (
    v >= 10 ? 'rgba(16,185,129,.7)' :
    v >= 0 ? 'rgba(16,185,129,.4)' :
    v >= -10 ? 'rgba(239,68,68,.35)' :
    'rgba(239,68,68,.7)'
  );
  const pmcChartData = useMemo(() => {
    const labels = pmcRows.map((row) => moment(row.date).format('DD/MM'));
    const ctl = pmcRows.map((row) => toFiniteNumber(row.ctl, 0));
    const atl = pmcRows.map((row) => toFiniteNumber(row.atl, 0));
    const tsb = pmcRows.map((row) => toFiniteNumber(row.tsb, 0));
    return {
      labels,
      datasets: [
        { type: 'line', label: 'CTL', data: ctl, borderColor: '#193255', backgroundColor: 'rgba(25,50,85,.07)', fill: true, tension: 0.4, borderWidth: 2.5, pointRadius: 0, order: 2 },
        { type: 'line', label: 'ATL', data: atl, borderColor: '#9ca3af', fill: false, tension: 0.4, borderWidth: 1.5, pointRadius: 0, borderDash: [5, 3], order: 3 },
        { type: 'bar', label: 'TSB', data: tsb, backgroundColor: tsb.map(tsbBarColor), borderRadius: 2, yAxisID: 'tsb', order: 1 },
      ],
    };
  }, [pmcRows]);
  const pmcChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { ticks: { font: { size: 9 }, maxTicksLimit: pmcRangeDays <= 30 ? 10 : pmcRangeDays <= 60 ? 8 : 6 } },
      y: { ticks: { font: { size: 9 } }, title: { display: true, text: 'CTL / ATL', font: { size: 9 } } },
      tsb: { position: 'right', ticks: { font: { size: 9 } }, grid: { drawOnChartArea: false }, title: { display: true, text: 'TSB', font: { size: 9 } } },
    },
  }), [pmcRangeDays]);

  const nextSevenTss = useMemo(() => getNextSevenDayTss(events), [events]);
  const nextSevenTssTotal = useMemo(
    () => nextSevenTss.reduce((sum, value) => sum + toFiniteNumber(value, 0), 0),
    [nextSevenTss]
  );
  const pmcProjection = useMemo(() => projectPmc(pmcLatest, nextSevenTss), [pmcLatest, nextSevenTss]);
  const pmcProjectedLast = pmcProjection.length ? pmcProjection[pmcProjection.length - 1] : null;
  const pmcTssTrackWidth = `${clamp((nextSevenTssTotal / 1000) * 100, 0, 100)}%`;

  if (loading) return <p>{t('athleteDashboard.loadingProfile')}</p>;

  const safeProfilePic =
    profileData?.profilePicture && String(profileData.profilePicture).trim() ? profileData.profilePicture : null;

  const go = (path) => {
    if (typeof onNavigateTo === 'function') { onNavigateTo(path); return; }
    navigate(path);
  };

  return (
    <div className="container-fluid athlete-dashboard-container">
      {!isTrainerView && (
        <header className="td-header">
          <div className="td-header-brand">
            <img
              src={safeProfilePic || undefined}
              alt={t('athleteDashboard.profilePhotoAlt')}
              className="td-header-avatar"
              onError={(e) => {
                e.currentTarget.src = `${(process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '')}/uploads/athletes/default.jpg`;
              }}
            />
            <span className="td-header-name">
              {profileData.firstName || profileData.lastName
                ? `${profileData.firstName} ${profileData.lastName}`.trim()
                : t('athleteDashboard.loadingProfile')}
            </span>
          </div>

          <h1 className="td-header-title">{t('athleteDashboard.title')}</h1>

          <nav className="td-header-nav">
            <button onClick={() => go('/social')} className="td-nav-btn td-nav-btn--community">
              {t('athleteDashboard.community')}
            </button>

            <button onClick={() => go('/athlete-profile')} className="td-nav-btn">
              {t('athleteDashboard.myProfile')}
            </button>

            <button onClick={() => go('/athlete-settings')} className="td-nav-btn">
              {t('athleteDashboard.settingsShort')}
            </button>

            <button
              onClick={() => { localStorage.clear(); go('/login'); }}
              className="td-nav-btn td-nav-btn--danger"
            >
              {t('athleteDashboard.logout')}
            </button>
          </nav>
        </header>
      )}

      <div className="row gx-3 px-3 mt-2 pb-3 tz20-athlete-dashboard-grid">
        <div className="col-12 col-lg-7 mb-3 tz20-athlete-main">
          <div className="tz20-week-selector">
            <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />
          </div>

          <div className="tz20-calendar-shell">
            <AthleteCalendar
              events={sanitizeEvents(events, t)}
              handleDateSelect={handleDateSelect}
              handleEventClick={handleEventClick}
              handleEventDrop={handleEventDrop}
              fetchEvents={fetchEvents}
              isTrainerView={isTrainerView}
              renderEventContent={renderEventContent}
              athleteId={userId}
            />
          </div>
          <small className="tz20-calendar-note">{t('athleteDashboard.calendarHint')}</small>

          <section className="tz20-pmc-card">
            <div className="tz20-card-head">
              <strong>{t('athleteDashboard.pmcTitle')}</strong>
              <div className="tz20-pmc-ranges">
                {[30, 60, 90].map((days) => (
                  <button
                    key={days}
                    type="button"
                    className={pmcRangeDays === days ? 'active' : ''}
                    onClick={() => setPmcRangeDays(days)}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
            <div className="tz20-pmc-legend">
              <span><i className="ctl" />CTL</span>
              <span><i className="atl" />ATL</span>
              <span><i className="tsb-pos" />TSB+</span>
              <span><i className="tsb-neg" />TSB-</span>
            </div>
            <div className="tz20-pmc-chart" aria-label="PMC visual">
              {pmcLoading && <div className="tz20-pmc-state">{t('pmcPane.loading')}</div>}
              {!pmcLoading && pmcError && <div className="tz20-pmc-state text-danger">{pmcError}</div>}
              {!pmcLoading && !pmcError && pmcRows.length === 0 && (
                <div className="tz20-pmc-state">{t('pmcPane.noData')}</div>
              )}
              {!pmcLoading && !pmcError && pmcRows.length > 0 && (
                <PmcChartReact type="bar" data={pmcChartData} options={pmcChartOptions} />
              )}
            </div>
            {pmcLatest && (
              <div className="tz20-pmc-kpis">
                <span>CTL <strong>{pmcLatest.ctl.toFixed(1)}</strong></span>
                <span>ATL <strong>{pmcLatest.atl.toFixed(1)}</strong></span>
                <span>TSB <strong>{pmcLatest.tsb.toFixed(1)}</strong></span>
              </div>
            )}
            <div className="tz20-whatif">
              <div className="tz20-whatif-title">{t('athleteDashboard.whatIfNext7')}</div>
              <div className="tz20-whatif-row">
                <span>{t('athleteDashboard.tss7d')}</span>
                <div className="tz20-whatif-track"><i style={{ width: pmcTssTrackWidth }} /></div>
                <b>{Math.round(nextSevenTssTotal)}</b>
              </div>
              <small>
                {pmcProjectedLast
                  ? t('athleteDashboard.projectedCtlLoad', {
                      ctl: pmcProjectedLast.ctl.toFixed(1),
                      tsb: pmcProjectedLast.tsb.toFixed(1),
                    })
                  : t('pmcPane.noData')}
              </small>
            </div>
          </section>
        </div>

        <div className="col-12 col-lg-5 d-flex flex-column gap-3 mb-3 tz20-athlete-side" style={{ minWidth: 0 }}>
          <section className="tz20-side-card tz20-summary-card">
            <div className="tz20-side-title">{t('athleteDashboard.weeklySummaryTitle')}</div>
            {showSummary ? (
              <>
                <WeeklySummary weeklySummary={weeklySummaryToShow} />
                {weeklySummaryDBLoading && (
                  <small style={{ opacity: 0.7, paddingLeft: 4 }}>{t('athleteDashboard.updatingSummary')}</small>
                )}
              </>
            ) : (
              <div className="tz20-summary-shell">
                {!weeklySummaryDBLoading && (
                  <small className="tz20-empty-note">{t('athleteDashboard.emptySummary')}</small>
                )}
              </div>
            )}
          </section>

          <section className="tz20-side-card tz20-weekly-chart-card">
            {showSummary ? (
              <WeeklySummaryChart weeklySummary={weeklySummaryToShow} defaultMetric="distance" height={210} />
            ) : (
              <small className="tz20-empty-note">{t('athleteDashboard.emptySummary')}</small>
            )}
          </section>

          <section className="tz20-side-card">
            <TrainingSummaryCard userId={userId} />
          </section>

          {!isTrainerView ? (
            <div className="coachbot-card tz20-coachbot-card">
              <div className="card-header">{t('athleteDashboard.coachAiAssistant')}</div>
              <div className="card-body">
                <AthleteCoachBot
                  athleteId={userId}
                  isTrainerView={isTrainerView}
                  athleteNameProp={`${profileData.firstName} ${profileData.lastName}`.trim() || null}
                />
              </div>
            </div>
          ) : (
            <>
              <section className="tz20-side-card" style={{ marginTop: 8 }}>
                <WellnessVsCargaCard athleteId={userId} athleteName={`${profileData.firstName} ${profileData.lastName}`.trim()} />
              </section>
            </>
          )}
        </div>
      </div>

      <ActivityModal
        show={showActivityModal}
        onHide={() => setShowActivityModal(false)}
        onSelectActivity={handleActivitySelection}
        athleteId={userId}
      />
      <CompetitionModal
        show={showCompetitionModal}
        onHide={() => setShowCompetitionModal(false)}
        date={selectedDate ? new Date(selectedDate).toISOString() : new Date().toISOString()}
        athleteId={userId}
        onSaved={() => { if (typeof fetchEvents === 'function') fetchEvents(); }}
      />

      {showDetailsModal && selectedActivity && (
        <ActivityDetailsModal
          key={selectedActivity?.id || selectedActivity?._id || 'activity-modal'}
          show={showDetailsModal}
          onHide={handleHideDetailsModal}
          activityType={selectedActivity}
          onSave={handleSaveDetails}
          onDelete={handleDelete}
          selectedDate={selectedActivity?.date || selectedDate}
          profilePicture={safeProfilePic || undefined}
          fetchEvents={fetchEvents}
          athleteId={userId}
        />
      )}
    </div>
  );
};

export default AthleteDashboard;
